import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import { RecoilRoot, useRecoilValue } from 'recoil';

import debounce from 'lodash/debounce';
import {
  Icon, Loader, ButtonGroup, Button, v2, TextEdit, Status,
} from '@fieldnation/platform-components';
import ProfileContent from './ProfileContent';
import QuestionSet from './Common/QuestionSet';
import Attributes from './Common/Attributes';
import FeedbackUpdateBanner from './Common/FeedbackUpdateBanner';
import {
  getFeedbackQuestions,
  getFeedbackQuestionsScore,
  sendFeedback,
  getWorkOrderDetails,
  calculateLateTime,
} from './utils';
import {
  EVENTS,
  EventTrackerSelector,
} from './amplitude';
import classes from './Modal.scss';
import {
  FeedbackScore,
  PrivateFeedbackModalProps,
  Question,
  QuestionsScore,
  WorkOrderDetails,
} from './types.d';

const { Modal } = v2;

const PrivateFeedbackModalContent = (props: PrivateFeedbackModalProps) => {
  const trackEvent = useRecoilValue(EventTrackerSelector);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsScore, setQuestionsScore] = useState<QuestionsScore>({});
  const [feedbackScore, setFeedbackScore] = useState<FeedbackScore[]>([]);
  const [feedbackDetails, setFeedbackDetails] = useState<string>('');
  const loadingCountRef = useRef(0);
  const [workorderDetails, setWorkorderDetails] = useState<WorkOrderDetails>(props);
  const [loading, setLoading] = useState(false);

  const startLoading = () => {
    loadingCountRef.current += 1;
    if (loadingCountRef.current > 0) {
      setLoading(true);
    }
  };

  const stopLoading = () => {
    loadingCountRef.current -= 1;
    if (loadingCountRef.current <= 0) {
      loadingCountRef.current = 0;
      setLoading(false);
    }
  };
  const [scores, setScores] = useState<FeedbackScore[]>([]);
  const [selectedAttribute, setSelectedAttribute] = useState<number[]>([]);
  const [isServiceDown, setIsServiceDown] = useState(false);
  const [showTextEdit, setShowTextEdit] = useState(false);

  const question = useMemo(
    () => questions?.find((q) => q?.questionId === feedbackScore?.[0]?.id)?.questionLabel,
    [questions, feedbackScore],
  );
  const hasFeedbackDetails = feedbackDetails?.trim()?.length > 0;
  const hasRequiredFeedback = workorderDetails?.statusId === 3
    ? (hasFeedbackDetails || selectedAttribute?.length > 0)
    : feedbackScore?.length > 0;

  const lateTime = useMemo(() => {
    const firstCheckIn = workorderDetails?.firstCheckIn || props?.firstCheckIn;
    if (firstCheckIn) {
      return calculateLateTime(firstCheckIn);
    }

    return null;
  }, [
    props?.firstCheckIn,
    workorderDetails?.firstCheckIn,
  ]);

  const onScoreChange = (value: FeedbackScore[]): void => {
    setFeedbackScore(value);
  };

  const handleFeedbackChange = (value: string): void => {
    const sanitized = (value || '').replace(/<(.|\n)*?>/g, '').trim();
    if (sanitized.length === 0) {
      setFeedbackDetails('');
      return;
    }
    setFeedbackDetails(value);
  };

  useEffect(() => {
    if (props?.workOrderId && !loading && questions?.length) {
      const isModifying = !!questionsScore?.scores?.length;
      let attributes = {};
      selectedAttribute.forEach((item) => {
        const attrName = questions?.[0]?.attributes?.find((i) => i?.attributeId === Math.abs(item))?.name;
        if (attrName) {
          attributes = {
            ...attributes,
            [attrName]: item > 0 ? 'Yes' : 'No',
          };
        }
      });
      trackEvent(EVENTS.CONSIDERED_LEAVING_PRIVATE_FEEDBACK, {
        Source: props?.source,
        'Work Order ID': props.workOrderId,
        'Work Order Status': workorderDetails.statusName,
        Version: '2',
        'Previously Reviewed': isModifying ? 'True' : 'False',
        ...(isModifying && {
          'Overall Service': questions?.[0]?.options?.find((i) => i?.value === feedbackScore[0]?.score)?.option,
          'Left Comment': feedbackDetails ? 'True' : 'False',
          ...(attributes),
          ...(!!feedbackDetails && { Comment: feedbackDetails }),
        }),
      });
    }
  }, [props?.workOrderId, loading]);

  useEffect(() => {
    if (questionsScore?.comment?.length) {
      setFeedbackDetails(questionsScore?.comment);
    } else if (questionsScore?.comment === '' || questionsScore?.comment === null) {
      setFeedbackDetails('');
    }
  }, [questionsScore?.comment]);

  useEffect(() => {
    if (questionsScore?.scores?.length) {
      const existingScore = (questionsScore?.scores ?? []).map(
        (score) => ({
          id: score?.questionId,
          score: score?.score,
        }),
      );
      onScoreChange(existingScore);
      setScores(existingScore);
    }

    if (questionsScore?.attributes?.length) {
      setSelectedAttribute(questionsScore?.attributes ?? []);
    }
  }, [questionsScore?.scores?.length, questionsScore?.attributes?.length, questions?.[0]?.questionId]);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        setShowTextEdit(true);
      }, 500);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading]);

  const fetchWorkOrderDetails = async (workOrderId: number) => {
    startLoading();
    try {
      const workOrder = await getWorkOrderDetails(workOrderId);

      if (!workOrder) {
        stopLoading();
        return;
      }

      const assignee = workOrder?.assignee?.user;
      const firstCheckIn = workOrder?.time_logs?.results?.find?.((log) => log?.first_check_in);

      setWorkorderDetails({
        workOrderTitle: workOrder?.title,
        typeOfWorkId: workOrder?.type_of_work?.id,
        assignee: {
          ...assignee,
          name: `${assignee?.first_name} ${assignee?.last_name}`,
        },
        companyId: workOrder?.company?.id,
        client: workOrder?.client,
        expireRateWindow: (workOrder?.ratings?.assigned_provider?.actions ?? [])?.includes('upgrade'),
        statusName: workOrder?.status?.name,
        statusId: workOrder?.status?.id,
        estimateHour: workOrder?.eta?.hour_estimate,
        problemsReported: workOrder?.problems?.metadata?.total ?? 0,
        timeZone: workOrder?.schedule?.time_zone?.name || 'America/New_York',
        loggedHours: workOrder?.time_logs?.hours || 0,
        firstCheckIn,
      });
    } catch (error) {
      // Error handled silently
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    const shouldFetchWorkOrder = props?.workOrderId && props?.source !== 'Work Order Details';
    if (shouldFetchWorkOrder) {
      fetchWorkOrderDetails(props.workOrderId);
    }
  }, [props?.source, props?.workOrderId]);

  const handleOnClose = (isTrackEvent = false, feedbackSource?: string): void => {
    if (isTrackEvent) {
      trackEvent(EVENTS.SHARED_PRIVATE_FEEDBACK, {
        'Provider ID': workorderDetails?.assignee?.id,
        'Company ID': workorderDetails?.companyId,
        'Work Order ID': props?.workOrderId,
        Question: question,
        Score: null,
        Source: props?.source,
      });
    }
    if (props?.onClose) props.onClose(feedbackSource);
  };

  const getAttributeLabel = (attributes: number[] = []): (string | undefined)[] => attributes?.map((a) => {
    const target = questions?.[0]?.attributes?.find((q) => Math.abs(q?.attributeId) === Math.abs(a));
    return target?.name;
  });

  const openSuccessModal = () => {
    if (props?.onOpenSuccessModal) props.onOpenSuccessModal();
  };

  const trackEventForAttributes = () => {
    const standOutAttributes = getAttributeLabel(selectedAttribute.filter((i) => i > 0));
    const areaForImprovement = getAttributeLabel(selectedAttribute.filter((i) => i < 0));
    const overallService = questions?.[0]?.options?.find((i) => i?.value === feedbackScore?.[0]?.score)?.option;
    const trackingData = {
      Source: props?.source,
      'Work Order ID': props.workOrderId,
      'Provider ID': workorderDetails?.assignee?.id,
      'Work Order Status': workorderDetails.statusName,
      ...(overallService ? { 'Overall Service': overallService } : {}),
      'Left Comment': feedbackDetails ? 'True' : 'False',
      'Left Stand Out Attributes': standOutAttributes?.length > 0 ? 'True' : 'False',
      'Left Areas For Improvement': areaForImprovement?.length > 0 ? 'True' : 'False',
    };

    trackEvent(EVENTS.LEFT_PRIVATE_FEEDBACK, {
      ...trackingData,
      ...(standOutAttributes?.length > 0 && { 'Stand Out Attributes': standOutAttributes }),
      ...(areaForImprovement?.length > 0 && { 'Areas For Improvement': areaForImprovement }),
    });
  };

  const onSubmit =
    debounce(async () => {
      const byBuyer = {
        id: window?.context?.id,
        firstName: window?.context?.first_name,
        lastName: window?.context?.last_name,
        thumbnail: window?.context?.thumbnail,
      };

      const payload: Record<string, unknown> = {
        questions: feedbackScore,
        companyId: workorderDetails?.companyId,
        workorderId: props?.workOrderId,
        title: workorderDetails?.workOrderTitle,
        typeOfWorkId: workorderDetails?.typeOfWorkId,
        identifier: props?.identifier,
        byUser: byBuyer,
        forUser: {
          id: workorderDetails?.assignee?.id,
        },
        comment: feedbackDetails,
        ...(workorderDetails?.client && { client: workorderDetails.client }),
      };

      if (selectedAttribute && selectedAttribute.length > 0) {
        payload.attributes = selectedAttribute;
      }

      if (props?.onUpdateFeedback) props.onUpdateFeedback(feedbackScore);

      await sendFeedback(payload);
      trackEventForAttributes();
      openSuccessModal();
      handleOnClose(false, props?.source);
    }, 500);

  useEffect(() => {
    if (props?.identifier && props?.workOrderId > 0) {
      startLoading();
      Promise.all([
        getFeedbackQuestions(props?.identifier),
        getFeedbackQuestionsScore(props?.workOrderId, props?.identifier),
      ])
        .then(([questionsData, scoresData]) => {
          if (questionsData?.length > 0) {
            setQuestions(questionsData);
          } else {
            setIsServiceDown(true);
          }

          if (Object.keys(scoresData).length === 0) {
            setQuestionsScore({
              comment: '',
            });
          } else {
            setQuestionsScore(scoresData);
          }
        })
        .catch(() => {
          setIsServiceDown(true);
        })
        .finally(() => {
          stopLoading();
        });
    }
  }, [props?.identifier, props?.workOrderId]);

  const getFooterContent = () => {
    if (isServiceDown) {
      return (
        <div className={classes.ModalFooter__Content}>
          <div>
            {props?.onBack && (
              <Button
                type="text"
                label="Back"
                onClick={props.onBack}
              />
            )}
          </div>
          <Button
            type="secondary"
            label="Close"
            className={classes.footer__step}
            onClick={() => handleOnClose()}
          />
        </div>
      );
    }
    return (
      <div className={classes.ModalFooter__Content}>
        <div>
          {props?.onBack && (
            <Button
              type="text"
              label="Back"
              onClick={props.onBack}
            />
          )}
        </div>
        <ButtonGroup textAlign="right">
          <Button
            type="secondary"
            label="Cancel"
            onClick={() => handleOnClose()}
          />
          <Button
            type="primary"
            label="Save Feedback"
            disabled={
                workorderDetails?.expireRateWindow ||
                loading ||
                !hasRequiredFeedback
              }
            onClick={() => {
              setLoading(true);
              onSubmit();
            }}
          />
        </ButtonGroup>
      </div>
    );
  };

  const getHeaderContent = () => (
    <div className={classes.ModalHeader}>
      <span className={classes.ModalHeader__Icon}>
        <Icon name="lockSolid" size={24} block />
      </span>
      <span className={classes.ModalHeader__Text}>
        Private Feedback
      </span>
    </div>
  );

  if (isServiceDown) {
    return (
      <TransitionGroup>
        <Modal
          onClose={handleOnClose}
          isOpen
          header={getHeaderContent()}
          size="medium"
          bottomDivider={false}
          style={{ padding: '0px' }}
          type="secondary"
        >
          <div>
            <ProfileContent
              {...workorderDetails?.assignee}
              workOrderId={props?.workOrderId}
              workOrderTitle={workorderDetails?.workOrderTitle}
              estimateHour={workorderDetails?.estimateHour}
              problemsReported={workorderDetails?.problemsReported}
              loggedHours={workorderDetails?.loggedHours}
              lateTime={lateTime}
            />
            <div className={classes.service__down}>
              <Status type="warning">Something went wrong! Please try again later.</Status>
            </div>
          </div>
          <div className={classes.ModalFooter}>
            {getFooterContent()}
          </div>
        </Modal>
      </TransitionGroup>
    );
  }

  return (
    <TransitionGroup>
      <Modal
        onClose={handleOnClose}
        isOpen
        header={getHeaderContent()}
        size="medium"
        bottomDivider={false}
        style={{ padding: '0px' }}
        type="secondary"
      >
        <div>
          <Loader isLoading={loading}>
            <section>
              <ProfileContent
                {...workorderDetails?.assignee}
                workOrderId={props?.workOrderId}
                workOrderTitle={workorderDetails?.workOrderTitle}
                estimateHour={workorderDetails?.estimateHour}
                problemsReported={workorderDetails?.problemsReported}
                loggedHours={workorderDetails?.loggedHours}
                lateTime={lateTime}
              />
              <div className={classes.MainContent}>
                <FeedbackUpdateBanner
                  updatedBy={questionsScore?.author?.name}
                  updatedDate={questionsScore?.dateAdded?.utc}
                  timeZone={workorderDetails?.timeZone}
                />
                {workorderDetails?.statusId !== 3 && (
                  <QuestionSet
                    questions={questions}
                    scores={scores}
                    expireRateWindow={workorderDetails?.expireRateWindow}
                    onScoreChange={onScoreChange}
                    setScores={setScores}
                  />
                )}
                <section>
                  <div className={classes.AddComment}>
                    &nbsp;Add a note for your team
                    <div className={classes.optional}> - Optional</div>
                  </div>
                  {showTextEdit && (
                    <TextEdit
                      disabled={workorderDetails?.expireRateWindow}
                      placeholder="Add notes about this provider to share with others in your organization."
                      value={feedbackDetails}
                      onChange={handleFeedbackChange}
                      height="80px"
                    />
                  )}
                </section>
                <Attributes
                  questions={questions}
                  setSelectedAttribute={setSelectedAttribute}
                  selectedAttribute={selectedAttribute}
                  statusId={workorderDetails?.statusId}
                />
                <div className={classes.InforArea__Text}>
                  Feedback is private to you and your organization and will never be
                  displayed to providers. You will see your feedback in match as you
                  vet providers.
                </div>
              </div>
            </section>
          </Loader>
          <div className={classes.ModalFooter}>
            {getFooterContent()}
          </div>
        </div>
      </Modal>
    </TransitionGroup>
  );
};

const PrivateFeedbackModal = (props: PrivateFeedbackModalProps): JSX.Element => (
  <RecoilRoot>
    <PrivateFeedbackModalContent {...props} />
  </RecoilRoot>
);

export default PrivateFeedbackModal;
